import React, { useState, useEffect, useCallback } from 'react';
import { formatDateTime } from '../utils/datetime';
import { ArrowLeft, ArrowRight, CheckCircle, Download, FileSignature, Send, Trash2 } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import signableDocsService from '../services/signableDocsService';
import { apiErrorText } from '../utils/apiError';

// Manual eSign — fill a lien document by hand, generate the PDF, send it for
// typed-name e-signature. For facilities not fully on mySMS360 (staff copy the
// case data over). Three sub-tabs (RBT / NOC / NPS), each a 3-step flow:
// input -> preview -> recipient + send.

const TAB_LABELS = { rbt: 'Release by Tenant', noc: 'Notice of Claim', nps: 'Notice of Public Sale' };

const fieldVisible = (f, values) => {
  if (!f.show_if) return true;
  return String(values[f.show_if.field] ?? '') === String(f.show_if.equals);
};

const inputType = (t) => (t === 'date' ? 'date' : t === 'datetime' ? 'datetime-local'
  : t === 'money' ? 'number' : 'text');

const DocFlow = ({ facilityId, docType }) => {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [step, setStep] = useState(1);          // 1 input, 2 preview, 3 send
  const [pdfUrl, setPdfUrl] = useState(null);
  const [docId, setDocId] = useState(null);
  const [recipient, setRecipient] = useState({ name: '', email: '' });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const load = useCallback(async () => {
    setStep(1); setPdfUrl(null); setDocId(null); setSent(false); setError(null);
    try {
      const { data } = await signableDocsService.fields(facilityId, docType);
      setFields(data.fields);
      // seed facility prefills
      const seed = {};
      data.fields.forEach((f) => { if (f.source === 'facility' && f.value) seed[f.key] = f.value; });
      setValues(seed);
    } catch (e) { setError('Could not load the document fields.'); }
  }, [facilityId, docType]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const generate = async () => {
    setBusy(true); setError(null);
    try {
      const res = await signableDocsService.preview(facilityId, docType, values);
      setPdfUrl(URL.createObjectURL(res.data));
      setStep(2);
    } catch (e) {
      setError(apiErrorText(e, 'Could not generate the document.'));
    } finally { setBusy(false); }
  };

  // Backend-reported email outcome (email_delivery) — a provider-rejected
  // sign-link email must be visible, never a silent "sent" (2026-09-01).
  const [emailFailed, setEmailFailed] = useState([]);

  const send = async () => {
    setBusy(true); setError(null);
    try {
      let id = docId;
      if (!id) {
        const { data } = await signableDocsService.create(facilityId, docType, values);
        id = data._id; setDocId(id);
      }
      const res = await signableDocsService.send(facilityId, id, recipient.name, recipient.email);
      setEmailFailed(res?.data?.email_delivery?.failed || []);
      setSent(true);
    } catch (e) {
      setError(apiErrorText(e, 'Could not send for signature.'));
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <CheckCircle className="w-12 h-12 text-emerald-500 mb-3" />
        <p className="text-[15px] font-medium text-slate-700">Sent for signature</p>
        <p className="text-[13px] text-slate-500 mt-1">
          {recipient.name} ({recipient.email}) will receive a link to review and sign.
        </p>
        {emailFailed.length > 0 && (
          <div className="mt-4 max-w-md px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-[13px] text-amber-800 text-left">
            The sign-link email to <strong>{emailFailed.join(', ')}</strong> was
            not accepted by the email service — the recipient has no signing
            link yet. Check the address and send again, or check the
            facility&rsquo;s email settings.
          </div>
        )}
        <button onClick={load} className="mt-5 text-[13px] text-blue-600 hover:underline">
          Prepare another {TAB_LABELS[docType]}
        </button>
      </div>
    );
  }

  return (
    <div>
      {error && <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-[13px]">{error}</div>}

      {step === 1 && (
        <div>
          <p className="text-[12px] text-slate-400 mb-4">
            Facility details are prefilled. Enter the tenant and case details (copy from your records).
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            {fields.filter((f) => fieldVisible(f, values)).map((f) => (
              <div key={f.key} className={f.type === 'textarea' ? 'col-span-2' : ''}>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">
                  {f.label}{f.required && <span className="text-red-400"> *</span>}
                  {f.source === 'facility' && <span className="text-slate-300 ml-1">(facility)</span>}
                </label>
                {f.type === 'select' ? (
                  <select value={values[f.key] || ''} onChange={(e) => set(f.key, e.target.value)}
                    className="w-full text-[13px] border border-slate-200 rounded-lg px-3 py-2">
                    <option value="">Select…</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === 'textarea' ? (
                  <textarea value={values[f.key] || ''} onChange={(e) => set(f.key, e.target.value)}
                    rows={2} className="w-full text-[13px] border border-slate-200 rounded-lg px-3 py-2" />
                ) : (
                  <input type={inputType(f.type)} value={values[f.key] || ''}
                    onChange={(e) => set(f.key, e.target.value)}
                    readOnly={f.source === 'facility'}
                    className={`w-full text-[13px] border border-slate-200 rounded-lg px-3 py-2 ${f.source === 'facility' ? 'bg-slate-50 text-slate-500' : ''}`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-6 flex justify-end">
            <button onClick={generate} disabled={busy}
              className="flex items-center gap-1.5 text-[13px] px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {busy ? 'Generating…' : 'Generate document'} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <iframe title="preview" src={pdfUrl} className="w-full h-[520px] rounded-lg border border-slate-200" />
          <div className="mt-5 flex justify-between">
            <button onClick={() => setStep(1)} className="flex items-center gap-1.5 text-[13px] text-slate-500 hover:underline">
              <ArrowLeft className="w-4 h-4" /> Edit details
            </button>
            <button onClick={() => setStep(3)} className="flex items-center gap-1.5 text-[13px] px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
              Looks good — continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="max-w-md">
          <p className="text-[13px] text-slate-600 mb-4">Send this document to the tenant for electronic signature.</p>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Recipient name <span className="text-red-400">*</span></label>
          <input value={recipient.name} onChange={(e) => setRecipient((p) => ({ ...p, name: e.target.value }))}
            className="w-full text-[13px] border border-slate-200 rounded-lg px-3 py-2 mb-3"
            placeholder="Must match the tenant's full name" />
          <label className="block text-[11px] font-medium text-slate-500 mb-1">Recipient email <span className="text-red-400">*</span></label>
          <input type="email" value={recipient.email} onChange={(e) => setRecipient((p) => ({ ...p, email: e.target.value }))}
            className="w-full text-[13px] border border-slate-200 rounded-lg px-3 py-2 mb-5" placeholder="tenant@example.com" />
          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="flex items-center gap-1.5 text-[13px] text-slate-500 hover:underline">
              <ArrowLeft className="w-4 h-4" /> Back to preview
            </button>
            <button onClick={send} disabled={busy || !recipient.name || !recipient.email}
              className="flex items-center gap-1.5 text-[13px] px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
              <Send className="w-4 h-4" /> {busy ? 'Sending…' : 'Send for signature'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const STATUS_STYLE = {
  signed: 'bg-green-50 text-green-700 ring-green-200',
  sent: 'bg-blue-50 text-blue-700 ring-blue-200',
  draft: 'bg-slate-50 text-slate-500 ring-slate-200',
  expired: 'bg-amber-50 text-amber-700 ring-amber-200',
  voided: 'bg-slate-50 text-slate-400 ring-slate-200',
};

// Send history. A lien notice is a legal artifact — its value is being able
// to show WHAT was sent, TO WHOM and WHEN, months later. The documents were
// already stored; nothing surfaced them.
const History = ({ facilityId, facilityTimezone }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.doc_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const { data } = await signableDocsService.list(facilityId, params);
      setRows(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load the history');
    } finally {
      setLoading(false);
    }
  }, [facilityId, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Open the stored PDF. Blob + object URL rather than a plain link, because
  // the endpoint needs the JWT the apiClient attaches.
  const openPdf = async (row, signed) => {
    try {
      const { data } = await signableDocsService.pdf(facilityId, row._id, signed);
      const url = URL.createObjectURL(data);
      window.open(url, '_blank', 'noopener');
      // Revoke late: revoking immediately can race the new tab's load.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not open the document');
    }
  };

  const removeDoc = async (row) => {
    const who = row.recipient_name || row.recipient_email || 'this document';
    const signed = row.status === 'signed' || row.signed_pdf_available;
    const msg = signed
      ? `Delete the signed ${TAB_LABELS[row.doc_type] || 'document'} for ${who}?

`
        + 'It stays recoverable — signed documents are archived, not destroyed, '
        + 'because they may be needed for a hearing.'
      : `Delete the ${TAB_LABELS[row.doc_type] || 'document'} for ${who}?

`
        + 'This one was never signed, so it will be removed for good.';
    if (!window.confirm(msg)) return;
    try {
      await signableDocsService.remove(facilityId, row._id);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not delete the document');
    }
  };

  const q = query.trim().toLowerCase();
  const visible = rows.filter((r) => {
    if (!q) return true;
    return [r.recipient_name, r.recipient_email, r.doc_type]
      .some((f) => (f || '').toLowerCase().includes(q));
  });

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search recipient or email"
          className="flex-1 min-w-[200px] rounded-lg border border-slate-200 px-3 py-1.5 text-[13px]"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-[13px]"
        >
          <option value="">All documents</option>
          {['rbt', 'noc', 'nps', 'history'].map((t) => (
            <option key={t} value={t}>{TAB_LABELS[t]}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-[13px]"
        >
          <option value="">Any status</option>
          {['draft', 'sent', 'signed', 'expired', 'voided'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {error && <p className="text-[13px] text-red-500 mb-3">{error}</p>}

      {loading ? (
        <p className="text-[13px] text-slate-400">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="text-[13px] text-slate-400">
          {rows.length === 0
            ? 'No documents have been prepared for this facility yet.'
            : 'No documents match those filters.'}
        </p>
      ) : (
        <div className="rounded-xl border border-slate-200/80 divide-y divide-slate-200/70">
          {visible.map((r) => (
            <div key={r._id} className="flex items-center gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-[13px] text-slate-800 truncate">
                  {TAB_LABELS[r.doc_type] || r.doc_type}
                  {r.recipient_name && (
                    <span className="text-slate-500"> · {r.recipient_name}</span>
                  )}
                </p>
                <p className="text-[11px] text-slate-400 truncate">
                  {r.recipient_email || 'Not sent to anyone yet'}
                  {' · '}
                  {formatDateTime(r.created_at, facilityTimezone)}
                  {r.signed_at && ` · signed ${formatDateTime(r.signed_at, facilityTimezone)}`}
                </p>
              </div>

              <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] ring-1 ${
                STATUS_STYLE[r.status] || STATUS_STYLE.draft}`}>
                {r.status}
              </span>

              <button
                onClick={() => openPdf(r, false)}
                className="shrink-0 inline-flex items-center gap-1 text-[12px] text-blue-600 hover:text-blue-700"
                title="Open the document that was sent"
              >
                <Download className="h-3.5 w-3.5" />
                PDF
              </button>

              {/* Only the signed copy carries the audit page — the part that
                  matters in a Chapter 59 proceeding. */}
              {r.signed_pdf_available && (
                <button
                  onClick={() => openPdf(r, true)}
                  className="shrink-0 text-[12px] text-green-700 hover:text-green-800"
                  title="Open the signed copy with its audit page"
                >
                  Signed
                </button>
              )}

              <button
                onClick={() => removeDoc(r)}
                className="shrink-0 text-slate-300 hover:text-red-500"
                title={r.status === 'signed' || r.signed_pdf_available
                  ? 'Archive this signed document'
                  : 'Delete this document'}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-slate-400 mt-3">
        Signing links expire 30 days after they are sent. An expired link does
        not invalidate a document that was already signed.
      </p>
    </div>
  );
};

const ManualEsign = () => {
  const { currentFacility } = useFacility();
  const facilityId = currentFacility?.id || currentFacility?._id;
  const [tab, setTab] = useState('rbt');

  if (!facilityId) return <div className="p-8 text-slate-400">Select a facility.</div>;

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center gap-2 mb-1">
        <FileSignature className="w-5 h-5 text-slate-700" />
        <h1 className="text-xl font-semibold text-slate-800">Manual Lien eSign</h1>
      </div>
      <p className="text-[13px] text-slate-500 mb-5">
        Prepare a lien document by hand and send it for electronic signature — for tenants and
        facilities not managed in mySMS360.
      </p>

      <div className="flex gap-1 mb-5 border-b border-slate-200">
        {['rbt', 'noc', 'nps', 'history'].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-[13px] font-medium border-b-2 -mb-px ${tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {t === 'history' ? 'History' : TAB_LABELS[t]}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white p-5">
        {tab === 'history' ? (
          <History
            facilityId={facilityId}
            facilityTimezone={currentFacility?.timezone}
          />
        ) : (
          <DocFlow key={`${facilityId}:${tab}`} facilityId={facilityId} docType={tab} />
        )}
      </div>
    </div>
  );
};

export default ManualEsign;
